import { createContext, useContext } from 'react';

export const supportedLanguages = ['en', 'cs', 'sk'] as const;
export type Language = (typeof supportedLanguages)[number];

export const defaultLanguage: Language = 'en';

/** Mirrors the colour-mode key, so a user's two display choices sit side by side. */
export const languageStorageKey = 'algobridge.language';

/**
 * Endonyms, deliberately never translated: somebody who has landed in the wrong language has to be
 * able to find their own in the list.
 */
export const languageLabels: Record<Language, string> = {
  en: 'English',
  cs: 'Čeština',
  sk: 'Slovenčina',
};

/**
 * Fixed BCP-47 tags for the Intl formatters. Pinned rather than left to the browser so a date reads
 * the same for everyone who picked the same language, and so the formatting is testable at all.
 */
export const intlLocales: Record<Language, string> = {
  en: 'en-GB',
  cs: 'cs-CZ',
  sk: 'sk-SK',
};

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (supportedLanguages as readonly string[]).includes(value);
}

export function readStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(languageStorageKey);
    return isLanguage(stored) ? stored : null;
  } catch {
    // Storage can be unavailable (private browsing, blocked cookies); detection still works.
    return null;
  }
}

export function storeLanguage(language: Language): void {
  try {
    localStorage.setItem(languageStorageKey, language);
  } catch {
    // As above — failing to remember the choice must not break making it.
  }
}

/**
 * The pure core of detection: an explicit choice wins, otherwise the first browser preference we
 * actually speak, otherwise English. Split out from the impure wrapper so it can be tested.
 */
export function chooseLanguage(stored: unknown, navigatorLanguages: readonly string[]): Language {
  if (isLanguage(stored)) return stored;

  for (const candidate of navigatorLanguages) {
    // Browsers report region-tagged values ('cs-CZ'); the language alone is what we match on.
    const language = candidate.split('-')[0]?.toLowerCase();
    if (isLanguage(language)) return language;
  }

  return defaultLanguage;
}

export function detectLanguage(): Language {
  const preferences =
    typeof navigator === 'undefined'
      ? []
      : (navigator.languages ?? (navigator.language ? [navigator.language] : []));

  return chooseLanguage(readStoredLanguage(), preferences);
}

export interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

export function useLanguage(): LanguageContextValue {
  const value = useContext(LanguageContext);
  if (!value) throw new Error('useLanguage must be used inside a LanguageProvider.');

  return value;
}
